import { format, isToday } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { logTimeSelectionConfirmed } from '@fhs/cart/src/utils/logging-utils';
import { Button, Text, tokens } from '@fhs/ui';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';

import { usePickupTimeOptions } from '../pickup-time-options-context';

import DayPicker from './day-picker';
import {
  findPickerSelectionForTime,
  getFireOrderInSecondsForPickupTime,
} from './earliest-pickup-time';
import PickupTimeModalHeading from './heading';
import NextDayWarning from './next-day-warning';
import TimePicker from './time-picker';

export function CartActionPickupTimeSelector({ onClose }: { onClose(): void }) {
  const { setFireOrderIn } = useOrderContext();
  const { logRBIEvent } = useCRMEventsContext();

  const {
    datePickerData,
    isLoadingData,
    firstAvailableDate,
    defaultPickupTime,
    displayPickupTime,
    slotRefreshTick,
    earliestPickupTimeInSeconds,
    todayStoreOpensAt,
    setConfirmedPickupTime,
    isRestaurantOpen,
    warningMessage,
  } = usePickupTimeOptions();

  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState('');
  const hasInitializedSelection = useRef(false);

  const updateFireOrderTime = useCallback(
    (selectedDateTime: Date) => {
      setFireOrderIn(getFireOrderInSecondsForPickupTime(selectedDateTime));
    },
    [setFireOrderIn]
  );

  // Restore the current display time when the modal opens (confirmed or earliest default).
  useEffect(() => {
    if (!datePickerData?.length || hasInitializedSelection.current) {
      return;
    }

    const initialTime = displayPickupTime ?? defaultPickupTime;
    if (!initialTime) {
      return;
    }

    const selection = findPickerSelectionForTime(datePickerData, initialTime);
    if (selection) {
      setSelectedSlot(selection.slotIso);
      setSelectedDay(selection.dayIndex);
    } else {
      setSelectedSlot(initialTime.toISOString());
      setSelectedDay(0);
    }

    hasInitializedSelection.current = true;
  }, [datePickerData, displayPickupTime, defaultPickupTime]);

  // Re-sync when the 5-minute filter drops past slots (e.g. confirmed time expired).
  useEffect(() => {
    if (!datePickerData?.length || !displayPickupTime || !hasInitializedSelection.current) {
      return;
    }

    const selection = findPickerSelectionForTime(datePickerData, displayPickupTime);
    if (selection) {
      setSelectedSlot(selection.slotIso);
      setSelectedDay(selection.dayIndex);
    }
  }, [slotRefreshTick, datePickerData, displayPickupTime]);

  const isTodaySelected = useMemo(() => {
    if (!datePickerData?.[selectedDay]?.date) return false;
    const today = format(new Date(), 'yyyy-MM-dd');
    return datePickerData[selectedDay].date === today;
  }, [datePickerData, selectedDay]);

  const shouldShowNextDayWarning =
    isRestaurantOpen && firstAvailableDate && !isToday(firstAvailableDate);

  if (isLoadingData) {
    return (
      <>
        <PickupTimeModalHeading onClose={onClose} />
        <View style={styles.emptyContainer}>
          <ActivityIndicator size="large" color={tokens.colors.$houseRed} />
          <Text.Ui size="md">Checking available time slots</Text.Ui>
        </View>
      </>
    );
  }

  if (!datePickerData || datePickerData.length === 0) {
    return (
      <>
        <PickupTimeModalHeading onClose={onClose} />
        <View style={styles.emptyContainer}>
          <Text.Ui size="lg">No available time slots</Text.Ui>
        </View>
      </>
    );
  }

  return (
    <>
      <PickupTimeModalHeading onClose={onClose} />

      <NextDayWarning
        shouldShowNextDayWarning={!!shouldShowNextDayWarning}
        firstAvailableDate={firstAvailableDate}
      />

      <DayPicker
        datePickerData={datePickerData}
        selectedDay={selectedDay}
        setSelectedDay={setSelectedDay}
      />

      <TimePicker
        datePickerData={datePickerData}
        selectedDay={selectedDay}
        selectedSlot={selectedSlot}
        setSelectedSlot={setSelectedSlot}
        earliestPickupTimeInSeconds={earliestPickupTimeInSeconds}
        todayStoreOpensAt={todayStoreOpensAt}
        warningMessage={isTodaySelected ? warningMessage : ''}
        isRestaurantOpen={isRestaurantOpen}
      />

      <View style={styles.container}>
        <Button
          loading={false}
          disabled={!selectedSlot}
          onPress={() => {
            if (selectedSlot) {
              const selectedDateTime = new Date(selectedSlot);
              const formattedTime = format(selectedDateTime, 'PPpp');
              setConfirmedPickupTime(selectedDateTime);
              updateFireOrderTime(selectedDateTime);
              logTimeSelectionConfirmed(logRBIEvent, formattedTime);
              onClose();
            }
          }}
        >
          <Button.Text>Continue</Button.Text>
        </Button>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingBottom: 18,
    marginHorizontal: 28,
  },

  emptyContainer: {
    padding: 28,
    alignItems: 'center',
    height: 324,
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 13,
  },
});

